import React from 'react'
import {
  InvoiceTableCell,
  InvoiceTableRow,
  StatusText,
  StatusWrapper,
} from 'Features/accountSettings/components/Billing/InvoicesTable/InvoiceTable.styled'
import { useTranslation } from 'react-i18next'
import { IconButton } from 'Components/Elements/IconButton/IconButton.styled'
import { Download } from 'Assets/icons/Download'
import { useGetInvoices } from 'Features/accountSettings/api/getInvoices'
import { Invoice } from 'Types/Invoice'
import { getAmountWithCurrency } from 'Utils/currencyHelper'
import { InvoicesNoResultsStyled, InvoicesRowsStyled } from './Invoices.styled'
import { useGetInvoiceDownloadLink } from 'Features/accountSettings/api/getInvoiceDownloadLink'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { ComponentLoader, theme } from '@clickcease/cc-client-components-lib'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle,
  faCircleExclamation,
  faHourglassEnd,
} from '@fortawesome/pro-light-svg-icons'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useLocalizedDate } from 'Hooks/useLocalizedDate'

type InvoicesTableProps = {
  numOfRows: number | 'all'
  sortBy: 'date' | 'payment' | 'amount' | 'status'
}

export const InvoicesRows = ({ numOfRows, sortBy }: InvoicesTableProps) => {
  const { t } = useTranslation('accountSettings')
  const invoices = useGetInvoices()

  const { mutate, isLoading } = useGetInvoiceDownloadLink()
  const { getLocalizedDate } = useLocalizedDate()
  const { showToaster } = useAppStore()
  const downloadInvoice = (invoiceId: number) => {
    showToaster(t('billing_page.invoices_table.download_success'), 'info')

    mutate(invoiceId, {
      onSuccess: (res) => {
        const isError = res instanceof Error
        if (!isError) {
          const anchor: HTMLAnchorElement = document.createElement('a')
          anchor.href = res.data.downloadLink
          anchor.download = 'invoice.pdf'
          anchor.click()
        } else {
          showToaster(t('billing_page.invoices_table.download_error'), 'failure')
        }
      },
      onError: () => {
        showToaster(t('billing_page.invoices_table.download_error'), 'failure')
      },
    })
  }

  const sortTable = React.useMemo(() => {
    if (invoices.data) {
      const numOfInvoices = numOfRows === 'all' ? invoices.data.length : numOfRows
      const afterSlice = invoices.data.slice(0, numOfInvoices)
      return [...afterSlice].sort((a, b) => b[sortBy] - a[sortBy]) || []
    } else {
      return []
    }
  }, [invoices.data, sortBy, numOfRows])

  const getStatusText = (status: string, amount: number) => {
    const text =
      status === 'Succeeded'
        ? amount >= 0
          ? t('billing_page.paid')
          : t('billing_page.refund')
        : status === 'Pending'
        ? t('billing_page.pending')
        : status === 'Failed'
        ? t('billing_page.failed')
        : ''

    return (
      <StatusText
        pending={status === 'Pending'}
        failed={status === 'Failed'}
        succeeded={status === 'Succeeded'}
      >
        {text}
      </StatusText>
    )
  }

  const renderInvoices = () => {
    return sortTable.map((invoice: Invoice) => (
      <InvoiceTableRow key={invoice.invoiceId}>
        <InvoiceTableCell>{getLocalizedDate(new Date(invoice.date * 1000))}</InvoiceTableCell>
        {invoice.status !== 'Failed' ? (
          <>
            <InvoiceTableCell>
              {invoice?.cardLast4Digits ? (
                <>**** {invoice?.cardLast4Digits}</>
              ) : invoice?.paymentMethod?.toLowerCase() === 'creditcard' ? (
                <>Credit card</>
              ) : (
                <>Wire transfer</>
              )}
            </InvoiceTableCell>
            <InvoiceTableCell>
              {invoice.amount && <>{getAmountWithCurrency(invoice.currency, invoice.amount)}</>}
            </InvoiceTableCell>
          </>
        ) : (
          <>
            <InvoiceTableCell></InvoiceTableCell>
            <InvoiceTableCell></InvoiceTableCell>
          </>
        )}
        <InvoiceTableCell status={true}>
          <StatusWrapper>
            <IconButton aria-label="status icon">
              {
                {
                  Pending: (
                    <FontAwesomeIcon
                      icon={faHourglassEnd as IconProp}
                      style={{ color: theme.colors.info.value }}
                    />
                  ),
                  Succeeded: (
                    <FontAwesomeIcon
                      icon={faCheckCircle as IconProp}
                      style={{ color: theme.colors.success.value }}
                    />
                  ),
                  Failed: (
                    <FontAwesomeIcon
                      icon={faCircleExclamation as IconProp}
                      style={{ color: theme.colors.warning.value }}
                    />
                  ),
                }[invoice.status]
              }
            </IconButton>
            {getStatusText(invoice.status, invoice.amount)}
          </StatusWrapper>

          {isLoading ? (
            <ComponentLoader css={{ width: '100px', height: '100px', position: 'relative' }} />
          ) : (
            invoice.status !== 'Failed' && (
              <IconButton
                aria-label="download invoice"
                onClick={() => {
                  downloadInvoice(invoice.invoiceId)
                }}
              >
                <Download />
              </IconButton>
            )
          )}
        </InvoiceTableCell>
      </InvoiceTableRow>
    ))
  }

  return (
    <>
      {invoices?.data?.length ? (
        <InvoicesRowsStyled>{renderInvoices()}</InvoicesRowsStyled>
      ) : (
        <InvoicesNoResultsStyled>
          {t('billing_page.invoices_table.empty_message')}
        </InvoicesNoResultsStyled>
      )}
    </>
  )
}
